// src/components/quiz/GuessPlayerMode.js
// Modalità "Indovina il giocatore": 10 round, indizi progressivi (ruolo,
// squadra, quotazione, presenze, media voto, gol/assist), l'utente scrive il
// nome con autocomplete. Meno indizi/tentativi usati = più punti.
import React, { useMemo, useRef, useState } from 'react';
import { Trophy, CheckCircle2, XCircle, Eye, Lock, Search, Coins, CalendarDays, Star, Target, Users } from 'lucide-react';
import { fmtNum, normalizeText } from './quizShared';
import { minCapsThreshold } from './quizGenerators';
import { RoleChip } from '../auction/auctionCommon';
import TeamBadge from '../TeamBadge';

export const TOTAL_ROUNDS = 10;
const BASE_SCORE = 100;
const PENALTY_PER_CLUE = 15;
const PENALTY_PER_WRONG_GUESS = 10;
const REVEAL_DELAY = 1800;
const MAX_SUGGESTIONS = 6;

const ROLE_LABELS = { P: 'Portiere', D: 'Difensore', C: 'Centrocampista', A: 'Attaccante' };

function buildClueList(player) {
  const isGk = String(player.role || '').trim().toUpperCase() === 'P';
  const roleCode = String(player.role || '').trim().toUpperCase();
  const clues = [
    { label: 'Ruolo', value: ROLE_LABELS[roleCode] || player.role || '—', type: 'role', roleCode },
    { label: 'Squadra', value: player.team || '—', type: 'team', team: player.team },
    { label: 'Quotazione', value: `${fmtNum(player.value)} cr`, icon: Coins },
    { label: 'Presenze', value: fmtNum(player.caps), icon: CalendarDays },
    { label: 'Media voto', value: fmtNum(player.markavg, 2), icon: Star },
    isGk
      ? { label: 'Gol subiti', value: fmtNum(player.gotgoals), icon: Target }
      : { label: 'Gol fatti', value: fmtNum(player.goals), icon: Target },
  ];
  if (!isGk) clues.push({ label: 'Assist', value: fmtNum(player.assists), icon: Users });
  return clues;
}

function getDepleteTier(score) {
  if (score >= 60) return 'high';
  if (score >= 30) return 'mid';
  return 'low';
}

function eligibleGuessPlayers(players) {
  const minCaps = minCapsThreshold(players);
  return players.filter(p => p.name && p.team && p.role && Number(p.caps || 0) >= minCaps);
}

// Usato dall'orchestratore per il controllo preliminare prima di avviare la
// modalità (stesso ruolo del check pickNextQuestion() per le altre modalità).
export function canPlayGuessMode(players) {
  return eligibleGuessPlayers(players).length > 0;
}

function pickRoundPlayers(players) {
  const eligible = eligibleGuessPlayers(players);
  const shuffled = [...eligible];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, Math.min(TOTAL_ROUNDS, shuffled.length));
}

function computeRoundScore(cluesRevealed, wrongGuesses) {
  const base = BASE_SCORE - (cluesRevealed - 1) * PENALTY_PER_CLUE;
  return Math.max(0, base - wrongGuesses * PENALTY_PER_WRONG_GUESS);
}

export default function GuessPlayerMode({ players, onFinish }) {
  const [roundPlayers] = useState(() => pickRoundPlayers(players));
  const [roundIdx,       setRoundIdx]       = useState(0);
  const [cluesRevealed,  setCluesRevealed]  = useState(1);
  const [wrongGuesses,   setWrongGuesses]   = useState(0);
  const [guessText,      setGuessText]      = useState('');
  const [roundOutcome,   setRoundOutcome]   = useState(null); // null | { correct, score }
  const [breakdown,      setBreakdown]      = useState([]);
  const [totalScore,     setTotalScore]     = useState(0);
  const [wrongFlash,     setWrongFlash]     = useState(false);

  const startedAtRef = useRef(Date.now());

  const target = roundPlayers[roundIdx];
  const clues = useMemo(() => (target ? buildClueList(target) : []), [target]);

  const suggestions = useMemo(() => {
    const q = normalizeText(guessText.trim());
    if (!q) return [];
    return players
      .filter(p => p.name && normalizeText(p.name).includes(q))
      .slice(0, MAX_SUGGESTIONS);
  }, [guessText, players]);

  if (!target) {
    return (
      <div className="quiz-panel glass" style={{ textAlign: 'center', padding: 32 }}>
        <p style={{ color: 'var(--muted)' }}>Impossibile trovare abbastanza giocatori per questa modalità.</p>
      </div>
    );
  }

  const goToNextRound = (entry, roundScore) => {
    const newBreakdown = [...breakdown, entry];
    const newTotalScore = totalScore + roundScore;

    window.setTimeout(() => {
      if (roundIdx + 1 >= roundPlayers.length) {
        onFinish({
          mode: 'guess',
          breakdown: newBreakdown,
          totalScore: newTotalScore,
          totalSecs: Math.round((Date.now() - startedAtRef.current) / 1000),
        });
        return;
      }
      setBreakdown(newBreakdown);
      setTotalScore(newTotalScore);
      setRoundIdx(i => i + 1);
      setCluesRevealed(1);
      setWrongGuesses(0);
      setGuessText('');
      setRoundOutcome(null);
    }, REVEAL_DELAY);
  };

  const submitGuess = (name) => {
    if (roundOutcome) return;
    const guessed = normalizeText(name);
    if (!guessed) return;

    if (guessed === normalizeText(target.name)) {
      const score = computeRoundScore(cluesRevealed, wrongGuesses);
      setRoundOutcome({ correct: true, score });
      goToNextRound({
        playerName: target.name,
        team: target.team,
        role: target.role,
        cluesUsed: cluesRevealed,
        wrongGuesses,
        correct: true,
        score,
      }, score);
      return;
    }

    setWrongGuesses(n => n + 1);
    setGuessText('');
    setWrongFlash(true);
    window.setTimeout(() => setWrongFlash(false), 500);
  };

  const handleReveal = () => {
    if (roundOutcome) return;
    setCluesRevealed(n => Math.min(n + 1, clues.length));
  };

  const handleGiveUp = () => {
    if (roundOutcome) return;
    setRoundOutcome({ correct: false, score: 0 });
    goToNextRound({
      playerName: target.name,
      team: target.team,
      role: target.role,
      cluesUsed: cluesRevealed,
      wrongGuesses,
      correct: false,
      score: 0,
    }, 0);
  };

  const maxPossibleNow = computeRoundScore(cluesRevealed, wrongGuesses);
  const allCluesRevealed = cluesRevealed >= clues.length;

  return (
    <div className="quiz-game quiz-game--guess">
      <div className="quiz-game__top">
        <div className="quiz-game__progress">
          <span>Round {roundIdx + 1} / {roundPlayers.length}</span>
          <div className="quiz-progress-bar">
            <div className="quiz-progress-fill" style={{ width: `${(roundIdx / roundPlayers.length) * 100}%` }} />
          </div>
        </div>
        <div className="quiz-game__stats">
          <div className="quiz-game__stat">
            <Trophy size={13} />
            <strong>{totalScore} pt</strong>
          </div>
        </div>
      </div>

      <div className="quiz-question glass">
        <p className="quiz-question__num">Chi è questo giocatore?</p>

        <div className="quiz-guess-deplete">
          <div className="quiz-guess-deplete__head">
            <strong>{maxPossibleNow}</strong>
            <span>punti disponibili in questo round</span>
          </div>
          <div className="quiz-guess-deplete__bar">
            <div
              className={`quiz-guess-deplete__fill quiz-guess-deplete__fill--${getDepleteTier(maxPossibleNow)}`}
              style={{ width: `${Math.max(maxPossibleNow, 0)}%` }}
            />
          </div>
        </div>

        <div className="quiz-guess-trail">
          {clues.slice(0, cluesRevealed).map((clue, i) => (
            <div key={clue.label} className={`quiz-guess-trail__item ${i === cluesRevealed - 1 ? 'is-latest' : ''}`}>
              {clue.type === 'role' ? (
                <RoleChip role={clue.roleCode} />
              ) : clue.type === 'team' ? (
                <TeamBadge team={clue.team} size={30} />
              ) : (
                <span className="quiz-guess-trail__icon"><clue.icon size={15} /></span>
              )}
              <div className="quiz-guess-trail__body">
                <span>{clue.label}</span>
                <strong>{clue.value}</strong>
              </div>
            </div>
          ))}

          {!allCluesRevealed && (
            <div className="quiz-guess-trail__locked">
              <Lock size={14} />
              <span>{clues[cluesRevealed].label} — rivela per scoprirlo</span>
            </div>
          )}
        </div>

        {!roundOutcome && !allCluesRevealed && (
          <button type="button" className="quiz-btn quiz-btn--ghost quiz-guess-nextclue" onClick={handleReveal}>
            <Eye size={14} /> Rivela il prossimo indizio (-{PENALTY_PER_CLUE} pt)
          </button>
        )}

        {roundOutcome && (
          <div className={`quiz-question__feedback ${roundOutcome.correct ? 'quiz-question__feedback--ok' : 'quiz-question__feedback--err'}`}>
            {roundOutcome.correct ? (
              <><CheckCircle2 size={15} /> Esatto! {target.name} — +{roundOutcome.score} pt</>
            ) : (
              <><XCircle size={15} /> Era {target.name} ({target.team})</>
            )}
          </div>
        )}
      </div>

      {!roundOutcome && (
        <div className="quiz-guess-input-wrap">
          <div className={`quiz-guess-input ${wrongFlash ? 'quiz-guess-input--wrong' : ''}`}>
            <Search size={16} className="quiz-guess-input__icon" />
            <input
              type="text"
              value={guessText}
              onChange={(e) => setGuessText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && suggestions[0]) submitGuess(suggestions[0].name);
              }}
              placeholder="Scrivi il nome del giocatore..."
              autoComplete="off"
            />
            {wrongGuesses > 0 && <span className="quiz-guess-input__hint">Non è lui, riprova ({wrongGuesses} tentativi)</span>}
          </div>

          {suggestions.length > 0 && (
            <div className="quiz-guess-suggestions">
              {suggestions.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="quiz-guess-suggestion"
                  onClick={() => submitGuess(p.name)}
                >
                  <strong>{p.name}</strong>
                  <span>{p.team}</span>
                </button>
              ))}
            </div>
          )}

          <button type="button" className="quiz-btn quiz-btn--ghost quiz-guess-giveup" onClick={handleGiveUp}>
            Mostra risposta
          </button>
        </div>
      )}
    </div>
  );
}
